import React from 'react';
import { Shield, Brain, Code, Leaf, CheckCircle, BookOpen, Wrench, Users, Award, Target } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../context/LanguageContext';

const TrainingsPage = () => {
  const { t } = useLanguage();

  const categoryConfig = [
    { key: 'cyber', icon: Shield, color: '#4DFF88' },
    { key: 'ai', icon: Brain, color: '#4DD6FF' },
    { key: 'software', icon: Code, color: '#FF4DA6' },
    { key: 'social', icon: Leaf, color: '#4DFF88' },
  ];

  const approachIcons = [Wrench, Target, BookOpen, Users, Award];

  return (
    <div className="min-h-screen bg-[#060b1a]">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b1a] via-[#0a1628] to-[#0f1f33]" />
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#4DFF88]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-[#4DD6FF]/10 rounded-full blur-[120px]" />
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full border border-[#4DFF88]/30 bg-[#4DFF88]/5 text-[#4DFF88] text-sm font-medium mb-6">
              {t('trainings.badge')}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
              {t('trainings.title')}
            </h1>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="relative py-16 bg-[#060b1a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm mb-16">
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('trainings.intro1')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed mb-6">
              {t('trainings.intro2')}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t('trainings.intro3')}
            </p>
          </div>

          {/* Training Categories */}
          <div className="space-y-8 mb-16">
            {categoryConfig.map((category, index) => {
              const items = t(`trainings.categories.${category.key}.items`);
              return (
                <div
                  key={index}
                  className="p-8 rounded-2xl border border-white/10 bg-white/5 hover:border-white/20 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <category.icon className="w-7 h-7" style={{ color: category.color }} />
                    </div>
                    <h2 className="text-2xl font-semibold text-white">
                      {t(`trainings.categories.${category.key}.title`)}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Array.isArray(items) && items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: category.color }} />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Our Approach */}
          <div className="p-8 rounded-2xl border border-[#4DFF88]/30 bg-gradient-to-br from-[#4DFF88]/10 to-transparent">
            <h2 className="text-2xl font-semibold text-white mb-2 text-center">
              {t('trainings.approachTitle')}
            </h2>
            <p className="text-[#4DFF88] text-center mb-8 font-medium">
              {t('trainings.approachSubtitle')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {(() => {
                const approachItems = t('trainings.approach');
                return Array.isArray(approachItems) && approachItems.map((item, index) => {
                  const IconComponent = approachIcons[index];
                  return (
                    <div key={index} className="text-center">
                      <div className="w-12 h-12 rounded-xl bg-[#4DFF88]/10 flex items-center justify-center mx-auto mb-3">
                        <IconComponent className="w-6 h-6 text-[#4DFF88]" />
                      </div>
                      <p className="text-sm text-gray-300">{item}</p>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrainingsPage;
